/**
 * MODULE: inferAssistantTopicFromMessage.ts
 * PURPOSE: Lightweight NL → topic slug hints for brain context query_type before stream routing returns.
 * Mirrors backend ``langflow_topic_dispatch.py`` NL backfill rules (UX preview only).
 * RECENT: 2026-06-06 [2.2.1316] — **`shouldSuppressAssistantNavigation`** for coaching C2 pipeline health (no SPA /deals misroute).
 * RECENT_PRIOR: 2026-06-06 [2.2.1311] — draft-follow-up, platform workspaces, create_contact NL preview parity.
 * RECENT_PRIOR: 2026-06-05 [2.2.1310] — Calendar + sales-hub draft decks NL parity.
 */

const ENRICH_RE = /\benrich\s+(?:contact\s+)?(.+)/i;
const BUILD_BRIEF_RE =
  /\bbuild\s+(?:an?\s+)?(?:account\s+)?brief\s+(?:for|on)\s+/i;
const WHO_IS_RE = /\bwho\s+is\s+/i;
/** Workspace / team roster — not person research. */
const WHO_IS_TEAM_ROSTER_RE =
  /\bwho\s+is\s+on\s+(?:the\s+)?(?:sales\s+hub\s+)?(?:workspace\s+)?team\b|\bwho(?:'s|\s+is)\s+on\s+(?:the\s+)?.{0,40}?\bteam\b|\bwho\s+(?:are\s+)?(?:the\s+)?(?:members?|users?)\b|\bworkspace\s+team\b|\blist\s+(?:workspace\s+)?members?\b/i;
const CRM_RESEARCH_BRIEF_RE =
  /\bresearch\s+brief\b|\bgive\s+me\s+a\s+research\s+brief\b|\bprep(?:are)?\s+me\s+(for|on)\b|\bmeeting\s+prep\b|\b(?:help\s+me\s+)?(?:better\s+)?understand\s+(?:the\s+)?(?:account|customer|company)\b|\bunderstand\s+(?:the\s+)?account\b/i;
const RESEARCH_COMPANY_RE = /\bresearch\s+\S/i;
const EXTERNAL_RESEARCH_RE =
  /\b(?:web|external|deep|full)\s+research\b|\bdue\s+diligence\b|\bcomprehensive\s+research\b/i;
const TELL_ME_ABOUT_RE = /\btell\s+me\s+about\b/i;
const PIPELINE_STAGE_RE = /\bpipeline\s+by\s+stage\b/i;
const TOP_ACCOUNTS_RE = /\btop\s+accounts\b/i;
const TOP_REPS_RE = /\btop\s+reps\b/i;
const SHOW_DEALS_RE =
  /\b(show|list|view)\s+(my\s+)?deals?\b|\bmy\s+deals?\b|\bdeals?\s+list\b|\bwhat\s+deals?\b/i;
const PIPELINE_KPI_RE =
  /\bhow many active deals\b|\btotal pipeline value\b|\bpipeline value\b|\bpipeline health\b|\bsummarize pipeline health\b/i;
const PIPELINE_RISKS_RE =
  /\bmain risks or gaps\b|\brisks?\s+or\s+gaps?\b.*\bpipeline\b|\bpipeline\b.*\brisks?\s+or\s+gaps?\b/i;
const FIND_CONTACT_RE = /\bfind contact\b|\bsearch contact\b|\blook up contact\b/i;
const FORECAST_RE =
  /\bforecast summary\b|\bpipeline forecast\b|\brevenue forecast\b/i;
const LOG_ACTIVITY_RE =
  /\blog\s+(?:an?\s+)?(?:activity|call|meeting|note)\b|\brecord\s+(?:an?\s+)?(?:activity|call|meeting)\b/i;
const CREATE_TASK_RE =
  /\bcreate\s+(?:a\s+)?task\b|\badd\s+(?:a\s+)?task\b|\bnew\s+task\b/i;
const UPDATE_DEAL_RE =
  /\bupdate\s+(?:the\s+)?(?:deal|opportunity)\b|\bmove\s+(?:the\s+)?(?:deal|opportunity)\b|\bchange\s+(?:deal\s+)?stage\b/i;
const STAGE_LOOKUP_RE = /\bstage\s+lookup\b|\blookup\s+stage\b|\bwhat\s+stage\b/i;
const EXPORT_REPORT_RE =
  /\bexport\s+(?:a\s+)?report\b|\bdownload\s+(?:a\s+)?report\b|\bexport\s+pipeline\b/i;
const LIST_CANVASES_RE =
  /\blist\s+(?:my\s+)?canvases\b|\bshow\s+(?:my\s+)?canvases\b|\bmanage\s+canvases\b/i;
const LIST_WIDGETS_RE =
  /\blist\s+(?:my\s+)?widgets\b|\bshow\s+(?:my\s+)?widgets\b|\bmanage\s+widgets\b/i;
const LIST_MEETINGS_RE =
  /\bschedule\s+(?:a\s+)?meeting\b|\blist\s+(?:my\s+)?meetings\b|\bupcoming\s+meetings\b|\bwhat\s+meetings?\s+do\s+i\s+have\b|\bmeetings?\s+do\s+i\s+have\b|\bmeetings?\s+(today|tomorrow|this\s+week)\b/i;
const SALES_HUB_CONTENT_RE =
  /\blist\s+my\s+draft\s+decks?\b|\blist\s+my\s+drafts?\b|\bsales\s+hub\s+library\b/i;
const DRAFT_FOLLOW_UP_RE =
  /\b(?:send|draft|write)\b.*\b(?:email|e-mail|follow[- ]?up|outreach)\b|\bemail\s+(?:to|for)\b|\bdraft\s+(?:a\s+)?(?:follow[- ]?up|outreach)\b/i;
/** Champion enablement copy — must win over generic draft-follow-up ("write an email… champion… forward"). */
const CHAMPION_INTERNAL_SELL_RE =
  /\bchampion\b.{0,60}\b(?:internal[\s-]?sell|forward|one[\s-]?pager|pitch|sell\s+internally|leadership|exec(?:utive)?s?)\b|\b(?:internal[\s-]?sell|one[\s-]?pager)\b.{0,40}\bchampion\b|\b(?:draft|write|give\s+me)\b.{0,40}\b(?:email|wording|content)\b.{0,40}\bchampion\b|\bchampion\b.{0,40}\b(?:enablement|enable)\b|\binternal[\s-]?sell\s+wording\b/i;
const REENGAGE_QUIET_CHAMPIONS_RE =
  /\bre-?engage\b.{0,40}\bquiet\s+champions?\b|\bquiet\s+champions?\b.{0,40}\bre-?engage\b/i;
const LIST_WORKSPACES_RE =
  /\b(list|show)\s+(me\s+)?(my\s+)?workspaces\b|\b(my|the)\s+workspaces\b/i;
const CREATE_CONTACT_RE =
  /\bcreate\s+(?:a\s+)?(new\s+)?contact\b|\badd\s+(?:a\s+)?(new\s+)?contact\b|\bnew\s+contact\b/i;
const EXPLICIT_NAV_VERB_RE =
  /\b(take\s+me\s+to|go\s+to|open(\s+the)?|navigate\s+to|show\s+me\s+the|bring\s+me\s+to|switch\s+to)\b/i;
const SUMMARIZE_PIPELINE_RE = /\b(summarize|summarise)\b.{0,100}\bpipeline\b/i;
const ANALYZE_PIPELINE_RE =
  /\banalyze\s+(the\s+)?pipeline\b|\bpipeline\s+(health|analysis|review|snapshot)\b/i;
const AT_RISK_PIPELINE_RE =
  /\b(at[- ]risk|stalled|stale)\s+deals?\b|\bwhich\s+deals?\s+are\s+at\s+risk\b|\bdeals?\s+are\s+at\s+risk\b/i;
const PATCH_SEGMENTATION_RE =
  /\b(?:which|what)\s+accounts?\b.{0,160}\b(?:stalled|expansion[- ]ready|expansion|high[- ]fit|low[- ]coverage|patch|portfolio)\b/i;

/** Strip optional `[QUERY_TYPE: …]` prefix before NL checks. */
function stripQueryTypePrefix(message: string): string {
  return (message || '').replace(/^\[QUERY_TYPE:\s*[^\]]+\]\s*/i, '').trim();
}

/**
 * Belt-and-suspenders: block SPA navigation for inline pipeline coaching (smoke C2).
 * Mirrors backend `_parse_navigation_destination` + `nl_sales_hub_kpi` analyze_pipeline guards.
 */
export function shouldSuppressAssistantNavigation(message: string): boolean {
  const text = stripQueryTypePrefix(message);
  if (!text) return false;
  if (EXPLICIT_NAV_VERB_RE.test(text)) return false;
  if (PIPELINE_KPI_RE.test(text)) return true;
  if (PIPELINE_RISKS_RE.test(text)) return true;
  if (SUMMARIZE_PIPELINE_RE.test(text)) return true;
  if (/\bpipeline\b/i.test(text) && /\bnext\s+best\s+actions?\b/i.test(text)) return true;
  if (ANALYZE_PIPELINE_RE.test(text)) return true;
  if (AT_RISK_PIPELINE_RE.test(text)) return true;
  if (/\bschedule\s+(?:a\s+)?discovery\s+recap\b/i.test(text)) return true;
  if (/\bbook\s+(?:a\s+)?discovery\s+call\b/i.test(text)) return true;
  if (/\blog\s+qualification\s+notes\b/i.test(text)) return true;
  if (/\bschedule\s+(?:a\s+)?negotiation\s+checkpoint\b/i.test(text)) return true;
  return false;
}

/**
 * Best-effort topic slug from user draft (for context/resolve query_type + topic chip preview).
 */
export function inferAssistantTopicFromMessage(message: string): string {
  const text = (message || '').trim();
  if (!text) return '';

  if (BUILD_BRIEF_RE.test(text)) return 'build-account-brief';
  if (ENRICH_RE.test(text)) return 'enrich-contact';
  if (CHAMPION_INTERNAL_SELL_RE.test(text)) return 'champion_internal_sell';
  if (REENGAGE_QUIET_CHAMPIONS_RE.test(text)) return 'reengage_quiet_champions';
  if (DRAFT_FOLLOW_UP_RE.test(text)) return 'draft-follow-up';
  if (LOG_ACTIVITY_RE.test(text)) return 'log-activity';
  if (CREATE_TASK_RE.test(text)) return 'create-task';
  if (UPDATE_DEAL_RE.test(text)) return 'update-opportunity';
  if (CREATE_CONTACT_RE.test(text)) return 'create_contact';
  if (WHO_IS_TEAM_ROSTER_RE.test(text)) return 'assistant_platform_tools';
  if (WHO_IS_RE.test(text)) return 'research-person';
  if (FIND_CONTACT_RE.test(text)) return 'research-person';
  if (STAGE_LOOKUP_RE.test(text)) return 'stage-lookup';
  if (CRM_RESEARCH_BRIEF_RE.test(text)) return 'company_research';
  if (TELL_ME_ABOUT_RE.test(text)) return 'company_research';
  if (RESEARCH_COMPANY_RE.test(text) && !WHO_IS_RE.test(text)) {
    return EXTERNAL_RESEARCH_RE.test(text) ? 'research-account' : 'company_research';
  }
  if (PIPELINE_STAGE_RE.test(text)) return 'pipeline-by-stage';
  if (TOP_ACCOUNTS_RE.test(text)) return 'top-accounts';
  if (TOP_REPS_RE.test(text)) return 'top-reps';
  if (PIPELINE_KPI_RE.test(text)) return 'pipeline-by-stage';
  if (SHOW_DEALS_RE.test(text)) return 'pipeline-by-stage';
  if (PIPELINE_RISKS_RE.test(text)) return 'forecast-summary';
  if (FORECAST_RE.test(text)) return 'forecast-summary';
  if (EXPORT_REPORT_RE.test(text)) return 'export-report';
  if (LIST_CANVASES_RE.test(text)) return 'manage-canvases';
  if (LIST_WIDGETS_RE.test(text)) return 'manage-widgets';
  if (LIST_WORKSPACES_RE.test(text)) return 'assistant_platform_tools';
  if (SALES_HUB_CONTENT_RE.test(text)) return 'sales-hub-content';
  if (LIST_MEETINGS_RE.test(text)) return 'schedule-meeting';
  if (PATCH_SEGMENTATION_RE.test(text)) return 'segment-account-patch';

  return '';
}
